import {
    Alert,
    Button,
    Checkbox,
    Group,
    Modal,
    ScrollArea,
    Stack,
    Text,
    TextInput,
} from '@mantine/core'
import {useEffect, useMemo, useState} from 'react'

import {formatBusinessTimestamp} from 'src/api/business-time'
import {formatPrice} from 'src/lib/format-price'

import {
    useCreateProjectRentalBillingCharges,
    useCreateRentalBillingCharges,
    useProjectRentalBillingPreview,
    useRentalBillingPreview,
} from './use-rental-billing'

type RentalBillingModalProps = {
    jobId?: string
    projectId: string
    opened: boolean
    onClose: () => void
    onChargesCreated: () => Promise<void>
    projectScoped?: boolean
    rentalStintIds?: string[]
}

function businessToday(): string {
    return formatBusinessTimestamp(new Date(), 'yyyy-MM-dd')
}

function parseDateInput(value: string): string | null {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        return null
    }
    return value
}

function pluralizeBillingUnit(unit: 'hour' | 'day' | 'week' | 'month', quantity: number): string {
    return `${unit}${quantity === 1 ? '' : 's'}`
}

function formatCoveredRange(start: string, end: string): string {
    return start === end ? start : `${start} through ${end}`
}

function formatPeriodSummary(
    quantity: number,
    unit: 'hour' | 'day' | 'week' | 'month',
    coveredStart: string,
    coveredEnd: string,
    each = false,
): string {
    return `${quantity} ${pluralizeBillingUnit(unit, quantity)}${each ? ' each' : ''} · ${formatCoveredRange(coveredStart, coveredEnd)}`
}

function describeGroupBilling(
    stintQuantities: number[],
    totalQuantity: number,
    unit: 'hour' | 'day' | 'week' | 'month',
): string {
    const deployedUnitCount = stintQuantities.length
    const firstQuantity = stintQuantities[0]
    const allDurationsMatch = stintQuantities.every((quantity) => quantity === firstQuantity)

    if (firstQuantity != null && allDurationsMatch) {
        return `${deployedUnitCount} deployed unit${deployedUnitCount === 1 ? '' : 's'} × ${firstQuantity} ${pluralizeBillingUnit(unit, firstQuantity)} each`
    }

    return `${totalQuantity} total unit-${pluralizeBillingUnit(unit, totalQuantity)} across ${deployedUnitCount} deployed units`
}

export function RentalBillingModal({
    jobId,
    projectId,
    opened,
    onClose,
    onChargesCreated,
    projectScoped = false,
    rentalStintIds,
}: RentalBillingModalProps) {
    const [billThroughValue, setBillThroughValue] = useState(businessToday)
    const [selectedStintIds, setSelectedStintIds] = useState<Set<string>>(new Set())
    const billThrough = parseDateInput(billThroughValue)
    const jobPreview = useRentalBillingPreview(jobId ?? '', billThrough, !projectScoped)
    const projectPreview = useProjectRentalBillingPreview(
        projectId,
        billThrough,
        rentalStintIds,
        projectScoped && opened,
    )
    const jobCharges = useCreateRentalBillingCharges(jobId ?? '', projectId)
    const projectCharges = useCreateProjectRentalBillingCharges(projectId)
    const preview = projectScoped ? projectPreview : jobPreview
    const createCharges = projectScoped ? projectCharges : jobCharges

    useEffect(() => {
        if (!opened) {
            setSelectedStintIds(new Set())
        }
    }, [opened])

    useEffect(() => {
        setSelectedStintIds(new Set())
    }, [billThroughValue])

    useEffect(() => {
        if (!opened || !preview.data) {
            return
        }
        setSelectedStintIds(
            new Set(
                preview.data.groups.flatMap((group) =>
                    group.stints.map((stint) => stint.rentalStintId),
                ),
            ),
        )
    }, [opened, preview.data])

    const selectedStints = useMemo(
        () =>
            preview.data?.groups
                .flatMap((group) => group.stints)
                .filter((stint) => selectedStintIds.has(stint.rentalStintId)) ?? [],
        [preview.data?.groups, selectedStintIds],
    )
    const selectedTotal = selectedStints.reduce((total, stint) => total + stint.totalPrice, 0)
    const allStintIds =
        preview.data?.groups.flatMap((group) => group.stints.map((stint) => stint.rentalStintId)) ??
        []
    const selectableItemCount =
        preview.data?.groups.reduce(
            (total, group) =>
                total + (group.stints[0]?.isUniquelyIdentified ? group.stints.length : 1),
            0,
        ) ?? 0
    const allStintsSelected =
        allStintIds.length > 0 && allStintIds.every((stintId) => selectedStintIds.has(stintId))

    function toggleStint(stintId: string, checked: boolean) {
        setSelectedStintIds((current) => {
            const next = new Set(current)
            if (checked) {
                next.add(stintId)
            } else {
                next.delete(stintId)
            }
            return next
        })
    }

    function toggleStintGroup(stintIds: string[], checked: boolean) {
        setSelectedStintIds((current) => {
            const next = new Set(current)
            for (const stintId of stintIds) {
                if (checked) {
                    next.add(stintId)
                } else {
                    next.delete(stintId)
                }
            }
            return next
        })
    }

    function toggleAllStints(checked: boolean) {
        setSelectedStintIds(new Set(checked ? allStintIds : []))
    }

    function handleClose() {
        setBillThroughValue(businessToday())
        setSelectedStintIds(new Set())
        createCharges.reset()
        onClose()
    }

    async function handleCreate() {
        if (!billThrough || selectedStintIds.size === 0) {
            return
        }
        await createCharges.mutateAsync({
            billThrough,
            rentalStintIds: [...selectedStintIds],
        })
        await onChargesCreated()
        handleClose()
    }

    return (
        <Modal opened={opened} onClose={handleClose} title="Add rental charges" size="lg">
            <Stack gap="lg">
                <TextInput
                    label="Bill through"
                    type="date"
                    value={billThroughValue}
                    onChange={(event) => setBillThroughValue(event.currentTarget.value)}
                />

                {preview.isLoading && <Text c="dimmed">Loading rental billing preview…</Text>}
                {preview.error && (
                    <Alert color="red" title="Could not load rental billing preview">
                        {preview.error instanceof Error
                            ? preview.error.message
                            : 'Please try again.'}
                    </Alert>
                )}
                {preview.data && preview.data.groups.length === 0 && (
                    <Text c="dimmed">No new rental periods are available for this cutoff.</Text>
                )}
                {selectableItemCount >= 5 && (
                    <Group justify="flex-end" gap="xs">
                        <Button
                            size="xs"
                            variant="subtle"
                            onClick={() => toggleAllStints(true)}
                            disabled={allStintsSelected}
                        >
                            Select all
                        </Button>
                        <Button
                            size="xs"
                            variant="subtle"
                            color="gray"
                            onClick={() => toggleAllStints(false)}
                            disabled={selectedStintIds.size === 0}
                        >
                            Clear all
                        </Button>
                    </Group>
                )}
                {preview.data && preview.data.groups.length > 0 && (
                    <ScrollArea.Autosize mah="55vh" type="auto" offsetScrollbars="y">
                        <Stack gap="lg" pr="xs">
                            {preview.data.groups.map((group) => {
                                const groupKey = `${group.jobId ?? 'job'}-${group.rentalAssetTypeId}-${group.billingType}-${group.billingRate}`
                                const isUniquelyIdentified =
                                    group.stints[0]?.isUniquelyIdentified ?? false
                                const stockStintIds = group.stints.map(
                                    (stint) => stint.rentalStintId,
                                )
                                const selectedGroupStints = group.stints.filter((stint) =>
                                    selectedStintIds.has(stint.rentalStintId),
                                )
                                const selectedGroupTotal = selectedGroupStints.reduce(
                                    (total, stint) => total + stint.totalPrice,
                                    0,
                                )
                                const stockGroupSelected =
                                    stockStintIds.length > 0 &&
                                    stockStintIds.every((stintId) => selectedStintIds.has(stintId))
                                const stockDurationIsUniform = group.stints.every(
                                    (stint) => stint.newQuantity === group.stints[0]?.newQuantity,
                                )
                                const stockPeriodQuantity = stockDurationIsUniform
                                    ? (group.stints[0]?.newQuantity ?? 0)
                                    : group.newQuantity
                                const groupDescription = describeGroupBilling(
                                    group.stints.map((stint) => stint.newQuantity),
                                    group.newQuantity,
                                    group.unit,
                                )

                                return (
                                    <Stack key={groupKey} gap="sm">
                                        <Group
                                            justify="space-between"
                                            align="flex-start"
                                            wrap="nowrap"
                                        >
                                            <div>
                                                <Text fw={500}>{group.rentalAssetTypeName}</Text>
                                                {group.jobName ? (
                                                    <Text size="sm" c="blue">
                                                        {group.jobName}
                                                    </Text>
                                                ) : null}
                                                <Text size="sm" c="dimmed">
                                                    {groupDescription}
                                                </Text>
                                            </div>
                                            <div>
                                                <Text size="sm" c="dimmed" ta="right">
                                                    {selectedGroupStints.length} of{' '}
                                                    {group.stints.length} selected
                                                </Text>
                                                <Text fw={500} ta="right">
                                                    {formatPrice(selectedGroupTotal)} of{' '}
                                                    {formatPrice(group.totalPrice)}
                                                </Text>
                                            </div>
                                        </Group>
                                        {isUniquelyIdentified ? (
                                            group.stints.map((stint) => {
                                                const selected = selectedStintIds.has(
                                                    stint.rentalStintId,
                                                )
                                                return (
                                                    <Checkbox.Card
                                                        key={stint.rentalStintId}
                                                        checked={selected}
                                                        onChange={(checked) =>
                                                            toggleStint(
                                                                stint.rentalStintId,
                                                                checked,
                                                            )
                                                        }
                                                        aria-label={`Select ${stint.rentalAssetDisplayName}`}
                                                        style={{
                                                            backgroundColor: selected
                                                                ? 'var(--mantine-color-blue-0)'
                                                                : undefined,
                                                            borderColor: selected
                                                                ? 'var(--mantine-color-blue-6)'
                                                                : undefined,
                                                            borderWidth: '2px',
                                                            padding: 'var(--mantine-spacing-md)',
                                                        }}
                                                    >
                                                        <Group
                                                            justify="space-between"
                                                            wrap="nowrap"
                                                            gap="md"
                                                        >
                                                            <Group
                                                                gap="md"
                                                                wrap="nowrap"
                                                                style={{flex: 1}}
                                                            >
                                                                <Checkbox.Indicator />
                                                                <div>
                                                                    <Text fw={500}>
                                                                        {
                                                                            stint.rentalAssetDisplayName
                                                                        }
                                                                    </Text>
                                                                    <Text size="sm" c="dimmed">
                                                                        {formatPeriodSummary(
                                                                            stint.newQuantity,
                                                                            group.unit,
                                                                            stint.coveredStart,
                                                                            stint.coveredEnd,
                                                                        )}
                                                                    </Text>
                                                                </div>
                                                            </Group>
                                                            <Text fw={500}>
                                                                {formatPrice(stint.totalPrice)}
                                                            </Text>
                                                        </Group>
                                                    </Checkbox.Card>
                                                )
                                            })
                                        ) : (
                                            <Checkbox.Card
                                                checked={stockGroupSelected}
                                                onChange={(checked) =>
                                                    toggleStintGroup(stockStintIds, checked)
                                                }
                                                aria-label={`Select all ${group.rentalAssetTypeName}`}
                                                style={{
                                                    backgroundColor: stockGroupSelected
                                                        ? 'var(--mantine-color-blue-0)'
                                                        : undefined,
                                                    borderColor: stockGroupSelected
                                                        ? 'var(--mantine-color-blue-6)'
                                                        : undefined,
                                                    borderWidth: '2px',
                                                    padding: 'var(--mantine-spacing-md)',
                                                }}
                                            >
                                                <Group
                                                    justify="space-between"
                                                    wrap="nowrap"
                                                    gap="md"
                                                >
                                                    <Group gap="md" wrap="nowrap" style={{flex: 1}}>
                                                        <Checkbox.Indicator />
                                                        <div>
                                                            <Text fw={500}>
                                                                {stockStintIds.length} deployed unit
                                                                {stockStintIds.length === 1
                                                                    ? ''
                                                                    : 's'}
                                                            </Text>
                                                            <Text size="sm" c="dimmed">
                                                                {formatPeriodSummary(
                                                                    stockPeriodQuantity,
                                                                    group.unit,
                                                                    group.coveredStart,
                                                                    group.coveredEnd,
                                                                    stockDurationIsUniform,
                                                                )}
                                                            </Text>
                                                        </div>
                                                    </Group>
                                                    <Text fw={500}>
                                                        {formatPrice(group.totalPrice)}
                                                    </Text>
                                                </Group>
                                            </Checkbox.Card>
                                        )}
                                    </Stack>
                                )
                            })}
                        </Stack>
                    </ScrollArea.Autosize>
                )}
                {preview.data &&
                    (preview.data.excludedStintCount > 0 ||
                        preview.data.fullyAllocatedStintCount > 0) && (
                        <Text size="sm" c="dimmed">
                            {preview.data.excludedStintCount > 0 &&
                                `${preview.data.excludedStintCount} non-billable stint${preview.data.excludedStintCount === 1 ? '' : 's'}. `}
                            {preview.data.fullyAllocatedStintCount > 0 &&
                                `${preview.data.fullyAllocatedStintCount} fully billed stint${preview.data.fullyAllocatedStintCount === 1 ? '' : 's'}.`}
                        </Text>
                    )}
                {createCharges.error && (
                    <Alert color="red" title="Could not create rental charges">
                        {createCharges.error instanceof Error
                            ? createCharges.error.message
                            : 'Please try again.'}
                    </Alert>
                )}
                <Group justify="flex-end">
                    <Button
                        variant="default"
                        onClick={handleClose}
                        disabled={createCharges.isPending}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={() => void handleCreate()}
                        disabled={selectedStintIds.size === 0 || !billThrough}
                        loading={createCharges.isPending}
                    >
                        Add rental charges
                        {selectedStints.length > 0 ? ` · ${formatPrice(selectedTotal)}` : ''}
                    </Button>
                </Group>
            </Stack>
        </Modal>
    )
}
